import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Druido (manuale V1, rr. 3047-3114).
 *
 * - **4 mosse automatiche** (`type: 'starting'`): *Preservato dalla Natura*,
 *   *Studiare l'Essenza*, *Nato dalla Terra*, *Lingua degli Spiriti*.
 * - **2 mosse di scelta** (`pickCount: 1`): *Divinità della Natura*,
 *   *Mutaforma*. Manuale r. 3088: "Inoltre, scegli una delle seguenti mosse".
 *
 * **Pattern critico — Casteggiante condizionato (D2 della PR)**:
 * - *Divinità della Natura* è la mossa che **attiva il casting**: il
 *   `services/spellAccess.ts` la riconosce come trigger della spell-list
 *   `druido-native`. Il PG che sceglie *Divinità della Natura* riceve come
 *   side-effect le mosse del Chierico *Lanciare Incantesimi Divini* e
 *   *Mana* (manuale r. 3103, semantica del tiro), ma per il filtraggio
 *   spell del builder basta il trigger di questa mossa.
 * - *Mutaforma* è alternativa non casteggiante. Il PG che la sceglie non
 *   ha accesso a incantesimi (la sezione spell mostra il placeholder).
 *
 * **Pattern critico — *Nato dalla Terra* (D3)**: mossa-config con `choices`
 * strutturate per le 10 Terre. Segno Rivelatore (testo libero) rimane in
 * description (FI-18 strutturerà più avanti i `choiceGroups` per gestire
 * scelte multi-gruppo + free-text).
 */

const preservatoDallaNatura: Move = {
  id: 'druido.preservato-dalla-natura',
  name: 'Preservato dalla Natura',
  type: 'starting',
  classId: 'druido',
  trigger: 'Quando una mossa ti dice di consumare una *razione*',
  description:
    'Non hai bisogno di bere o di mangiare. Se una mossa ti dice di consumare una *razione*, ignora tale prescrizione.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const studiareLEssenza: Move = {
  id: 'druido.studiare-l-essenza',
  name: "Studiare l'Essenza",
  type: 'starting',
  classId: 'druido',
  trigger: 'Quando passi del tempo a contemplare uno spirito animale',
  description:
    'Puoi aggiungere la sua forma a quelle che puoi assumere con *Mutaforma* e la sua specie a quelle con cui puoi comunicare usando *Lingua degli Spiriti*.\n\n**NB**: la lista delle forme/specie apprese vive a runtime nelle note del PG (gestite dal giocatore). Non si modella in dati strutturati a livello L1.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const natoDallaTerra: Move = {
  id: 'druido.nato-dalla-terra',
  name: 'Nato dalla Terra',
  type: 'starting',
  classId: 'druido',
  trigger: 'Alla creazione del personaggio (configurazione della Terra natale)',
  description:
    'Sei entrato in sintonia con un luogo dove gli spiriti sono forti e antichi, ed essi ti hanno marchiato come uno di loro. Non importa dove tu vada, la connessione con la tua Terra e i suoi spiriti vive dentro di te, dandoti la possibilità di attingere alle forme e alle capacità della natura selvaggia.\n\nScegli la **Terra** con cui sei in armonia.\n\nScegli, inoltre, un **Segno Rivelatore** che rifletta lo spirito della tua Terra, un attributo fisico che ti distingua come *Nato da quella Terra*. Può essere una caratteristica animale, come corna di cervo o macchie di leopardo, o qualcosa di più generale, tipo occhi di cristallo lucente o capelli come foglie. Il tuo Segno Rivelatore rimane, non importa quale forma tu prenda.',
  tags: [],
  prerequisites: [],
  replaces: [],
  choices: [
    { id: 'grandi-foreste', label: 'le Grandi Foreste' },
    { id: 'pianure-sussurranti', label: 'le Pianure Sussurranti' },
    { id: 'vasto-deserto', label: 'il Vasto Deserto' },
    { id: 'delta-del-fiume', label: 'il Delta del Fiume' },
    { id: 'profondita-della-terra', label: 'le Profondità della Terra' },
    { id: 'isole-di-zaffiro', label: 'le Isole di Zaffiro' },
    { id: 'mare-aperto', label: 'il Mare Aperto' },
    { id: 'montagne-torreggianti', label: 'le Montagne Torreggianti' },
    { id: 'circolo-artico', label: 'il Circolo Artico' },
    { id: 'arida-steppa', label: "l'Arida Steppa" },
  ],
  choiceCount: 1,
  narrativeFields: [
    {
      id: 'segno-rivelatore',
      label: 'Segno Rivelatore',
      placeholder:
        'es. corna di cervo, macchie di leopardo, occhi di cristallo lucente, capelli come foglie…',
      required: true,
      multiline: true,
    },
  ],
};

const linguaDegliSpiriti: Move = {
  id: 'druido.lingua-degli-spiriti',
  name: 'Lingua degli Spiriti',
  type: 'starting',
  classId: 'druido',
  trigger:
    "Quando comunichi con animali originari di una delle tue Terre, o di cui hai *Studiato l'Essenza*",
  description:
    "I grugniti, i latrati, i cinguettii e gli altri versi delle creature selvagge sono per te come una lingua. Puoi comprendere e farti comprendere da qualunque animale originario di una delle tue Terre, o di cui hai potuto *Studiare l'Essenza*.\n\nSe prendi questa mossa con una mossa multiclasse, ottieni anche *Nato dalla Terra* e *Studiare l'Essenza*.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const divinitaDellaNatura: Move = {
  id: 'druido.divinita-della-natura',
  name: 'Divinità della Natura',
  type: 'starting',
  classId: 'druido',
  trigger: 'Alla creazione del personaggio (alternativa: *Mutaforma*)',
  description:
    "La Sfera d'Influenza e la Tradizione Sacra del dio che servi e veneri sono quelle della *Natura*. Scegli un **Fondamento** per il suo culto e indica il nome della divinità.\n\nOttieni le mosse del Chierico *Lanciare Incantesimi Divini* e ***Mana***, ma non *Divinità*. Sebbene questa *non si consideri multiclasse*, per determinare il tuo livello da Chierico segui le normali regole sulle mosse multiclasse.\n\n*Non puoi prendere* questa mossa se hai già *Divinità* (Chierico) o *Ordine Sacro* (Paladino).",
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18 Fase 2: aggiunto `choices` shorthand per il Fondamento del culto
  // (6 opzioni, pickCount 1 — coerente con Nato dalla Terra/Sentieri del
  // Monaco che usano lo shorthand single-group). Il `narrativeField` per il
  // nome della divinità è invariato (era già in Fase 1).
  choices: [
    {
      id: 'sacrificio-se',
      label: 'Sacrificare parte di sé o offrire il proprio dolore',
      description: 'Corpo, mente o spirito.',
    },
    { id: 'voti-rinuncia', label: 'Imporsi e mantenere voti di rinuncia' },
    {
      id: 'sacrificio-altri',
      label: 'Sacrificare il corpo o l’anima di altri esseri viventi',
      description: 'O loro simulacri.',
    },
    {
      id: 'benevolenza-pieta',
      label: 'Farsi portatore della benevolenza o della pietà divina',
    },
    {
      id: 'collera-vendetta',
      label: 'Farsi portatore della collera o della vendetta divina',
    },
    { id: 'rituali-liturgici', label: 'Compiere complessi rituali liturgici' },
  ],
  choiceCount: 1,
  narrativeFields: [
    {
      id: 'nome-divinita-natura',
      label: 'Nome della divinità della Natura',
      placeholder: 'es. Maribeth, Elone, Drusa…',
      required: true,
      multiline: false,
    },
  ],
  // **FI-38 Opz. C (2026-05-14)**: bundle cross-class verso Chierico
  // (manuale V1 r. 3103: «Ottieni le mosse del Chierico *Lanciare
  // Incantesimi Divini* e *Mana*, ma non *Divinità*»). Il Druido casta
  // riusando letteralmente le mosse del Chierico (roll +SAG ereditato).
  // La spell-list `druido-native` è discriminata dalla presenza di
  // questa mossa-padre nel PG, mentre il trigger tecnico del casting è
  // `chierico.lanciare-incantesimi-divini` (uniformazione cross-classe
  // delle 3 casteggianti "Divine").
  bundles: ['chierico.lanciare-incantesimi-divini', 'chierico.mana'],
};

const mutaforma: Move = {
  id: 'druido.mutaforma',
  name: 'Mutaforma',
  type: 'starting',
  classId: 'druido',
  trigger: 'Quando invochi gli spiriti per cambiare la tua forma',
  description:
    "Tira+**SAG**. Con 10+, ottieni 3 prese. Con 7-9, ne ottieni 2. Con 6-, qualcosa va veramente male, ma ottieni comunque 1 presa.\n\nPuoi assumere la forma di qualunque animale di cui tu abbia *Studiato l'Essenza* o che viva in una delle tue Terre, trasformandoti in una sua perfetta copia, assieme agli oggetti che porti con te. Mentre sei trasformato usi le tue normali statistiche, ma acquisisci qualunque capacità o debolezza innata della forma (artigli, ali, respirare acqua invece che aria, dimensioni, ecc.). Il GM ti dirà anche una o più mosse che rappresentano ciò che la forma assunta sa fare per istinto: per usarle basta che tu spenda una presa, e la mossa funzionerà automaticamente senza bisogno di tirare.\n\nQuando finisci le prese (sia che tu le abbia usate o che decida di sbarazzartene), ritorni alla tua forma normale.\n\nSe prendi questa mossa con una mossa multiclasse, ottieni anche *Nato dalla Terra* e *Studiare l'Essenza*.\n\n*Alternativa non casteggiante a Divinità della Natura: chi la sceglie non ha accesso a incantesimi.*",
  roll: {
    stat: 'saggezza',
    on10plus: { text: '3 prese da spendere per attivare le mosse della forma assunta.' },
    on7to9: { text: '2 prese.' },
    on6minus: { text: 'Qualcosa va male, ma ottieni 1 presa.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (4): prese tutte al livello 1 senza scelta.
 */
export const druidoStartingAutoMoves: readonly Move[] = [
  preservatoDallaNatura,
  studiareLEssenza,
  natoDallaTerra,
  linguaDegliSpiriti,
] as const;

/**
 * Mosse iniziali di SCELTA (2): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`). *Divinità della Natura* abilita il casting; *Mutaforma*
 * no.
 */
export const druidoStartingChoiceMoves: readonly Move[] = [divinitaDellaNatura, mutaforma] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const druidoInitialMoves: readonly Move[] = [
  ...druidoStartingAutoMoves,
  ...druidoStartingChoiceMoves,
];
